/**
 * VerifyEmailPage — /verify-email/:token. Users land here from the
 * verification email. Confirms the token with the backend and shows a
 * friendly success (or "expired") state.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, ok: false, already: false, error: '' });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/verify-email/${token}`);
        setState({ loading: false, ok: !!data.ok, already: !!data.already_verified, error: '' });
      } catch (e) {
        setState({ loading: false, ok: false, already: false, error: e?.response?.data?.detail || 'Verification failed' });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-emerald-100 p-8 text-center">
        {state.loading ? (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-emerald-500 animate-spin mb-4" />
            <h1 className="text-xl font-semibold text-slate-900">Verifying your email…</h1>
          </>
        ) : state.ok ? (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500 mb-4" />
            <h1 className="text-2xl font-semibold text-slate-900">{state.already ? 'Already verified' : 'Email verified'}</h1>
            <p className="text-sm text-slate-500 mt-2">Your 7-day trial credits and AI features are unlocked.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-6 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="verify-email-continue">
              Go to dashboard
            </Button>
          </>
        ) : (
          <>
            <XCircle className="h-14 w-14 mx-auto text-rose-500 mb-4" />
            <h1 className="text-xl font-semibold text-slate-900">Verification link invalid</h1>
            <p className="text-sm text-slate-500 mt-2">{state.error || 'The link may have expired.'} Sign in and request a fresh verification email.</p>
            <Link to="/login"><Button className="mt-6" variant="outline">Back to sign in</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * VerifyEmailBanner — subtle in-app banner shown at the top of the shell
 * whenever the logged-in user has email_verified === false. Provides a
 * one-click "resend" action.
 */
export function VerifyEmailBanner({ user, onResend }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user || user.email_verified) return null;

  const resend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`);
      setSent(true);
      if (onResend) onResend();
    } catch { /* silent */ }
    setSending(false);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 flex items-center gap-3 text-xs" data-testid="verify-email-banner">
      <Mail className="h-4 w-4 shrink-0" />
      <span className="flex-1">Verify your email to unlock trial credits and AI features. We sent a link to <strong>{user.email}</strong>.</span>
      {sent ? (
        <span className="text-emerald-700 font-medium">Sent — check your inbox</span>
      ) : (
        <button onClick={resend} disabled={sending} className="font-semibold underline hover:text-amber-900 disabled:opacity-60" data-testid="verify-email-resend">
          {sending ? 'Sending…' : 'Resend'}
        </button>
      )}
    </div>
  );
}
